import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import {
  Star, Shield, Settings, RefreshCw, Loader2, X, Search,
  Building2, MapPin, Calendar, DollarSign, CheckCircle, Sparkles, Gem,
  TrendingUp, Users, Award
} from 'lucide-react';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, label, value, color }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="h-6 w-6" />
    </div>
    <div>
      <p className="text-2xl font-black text-gray-900">{value}</p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  </div>
);

export default function UltraFeaturedListings() {
  const { token } = useAuth();
  const [tab, setTab] = useState('featured');
  const [featured, setFeatured] = useState([]);
  const [verified, setVerified] = useState([]);
  const [featuredConfig, setFeaturedConfig] = useState({ price_usd: 299, duration_days: 30, is_active: true });
  const [verifiedConfig, setVerifiedConfig] = useState({ price_usd: 299, duration_days: 365, is_active: true });
  const [commissionConfig, setCommissionConfig] = useState({ rate_percent: 3.0, min_fee_usd: 10, is_active: true });
  const [eventListingConfig, setEventListingConfig] = useState({ price_usd: 199, duration_days: 90, is_active: true });
  const [analyticsPremiumConfig, setAnalyticsPremiumConfig] = useState({ price_usd: 49, duration_days: 30, is_active: true });
  const [eventListings, setEventListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [manualModal, setManualModal] = useState(null); // 'featured' | 'verified'
  const [manualSearch, setManualSearch] = useState('');
  const [allOrgs, setAllOrgs] = useState([]);
  const [orgsLoading, setOrgsLoading] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const [featRes, verRes, fcRes, vcRes, commRes, evRes, apRes, evlRes] = await Promise.all([
        axios.get(`${API}/ultra/featured-contractors`, { headers }),
        axios.get(`${API}/ultra/verified-contractors`, { headers }),
        axios.get(`${API}/ultra/featured-listing-config`, { headers }),
        axios.get(`${API}/ultra/verified-badge-config`, { headers }),
        axios.get(`${API}/ultra/offer-commission-config`, { headers }),
        axios.get(`${API}/ultra/event-listing-config`, { headers }),
        axios.get(`${API}/ultra/analytics-premium-config`, { headers }),
        axios.get(`${API}/ultra/event-listings`, { headers }),
      ]);
      setFeatured(featRes.data?.contractors || []);
      setVerified(verRes.data?.contractors || []);
      setFeaturedConfig(fcRes.data || {});
      setVerifiedConfig(vcRes.data || {});
      setCommissionConfig(commRes.data || {});
      setEventListingConfig(evRes.data || {});
      setAnalyticsPremiumConfig(apRes.data || {});
      setEventListings(evlRes.data?.listings || []);
    } catch (e) {
      toast.error('Veri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const loadOrgs = async () => {
    setOrgsLoading(true);
    try {
      const res = await axios.get(`${API}/ultra/organizations?limit=200&role=CONTRACTOR`, { headers });
      setAllOrgs(res.data?.organizations || []);
    } catch { setAllOrgs([]); }
    finally { setOrgsLoading(false); }
  };

  const openManualModal = (type) => {
    setManualModal(type);
    setManualSearch('');
    setSelectedOrg(null);
    loadOrgs();
  };

  const handleManualAction = async () => {
    if (!selectedOrg) return;
    try {
      if (manualModal === 'featured') {
        await axios.post(`${API}/ultra/featured-contractors/${selectedOrg.id}/feature`,
          { note: 'Manuel — Admin' }, { headers });
        toast.success(`${selectedOrg.display_name} öne çıkarıldı`);
        setFeatured(prev => [...prev, { ...selectedOrg, featured_until: new Date(Date.now() + featuredConfig.duration_days * 86400000).toISOString() }]);
      } else {
        await axios.post(`${API}/ultra/verified-contractors/${selectedOrg.id}/verify`,
          { note: 'Manuel — Admin' }, { headers });
        toast.success(`${selectedOrg.display_name} doğrulandı`);
        setVerified(prev => [...prev, { ...selectedOrg, verified_until: new Date(Date.now() + verifiedConfig.duration_days * 86400000).toISOString() }]);
      }
      setManualModal(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleRemoveFeatured = async (orgId, name) => {
    try {
      await axios.delete(`${API}/ultra/featured-contractors/${orgId}/feature`, { headers });
      toast.success(`${name} öne çıkarmadan kaldırıldı`);
      setFeatured(prev => prev.filter(c => c.id !== orgId));
    } catch { toast.error('İşlem başarısız'); }
  };

  const handleRemoveVerified = async (orgId, name) => {
    try {
      await axios.delete(`${API}/ultra/verified-contractors/${orgId}/verify`, { headers });
      toast.success(`${name} rozeti kaldırıldı`);
      setVerified(prev => prev.filter(c => c.id !== orgId));
    } catch { toast.error('İşlem başarısız'); }
  };

  const saveFeaturedConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/ultra/featured-listing-config`, { config: featuredConfig }, { headers });
      toast.success('Öne çıkarma ayarları kaydedildi');
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const saveVerifiedConfig = async () => {
    setSaving(true);
    try {
      await Promise.all([
        axios.put(`${API}/ultra/verified-badge-config`, { config: verifiedConfig }, { headers }),
        axios.put(`${API}/ultra/offer-commission-config`, { config: commissionConfig }, { headers }),
        axios.put(`${API}/ultra/event-listing-config`, { config: eventListingConfig }, { headers }),
        axios.put(`${API}/ultra/analytics-premium-config`, { config: analyticsPremiumConfig }, { headers }),
      ]);
      toast.success('Tüm ayarlar kaydedildi');
    } catch { toast.error('Kayıt başarısız'); }
    finally { setSaving(false); }
  };

  const filteredFeatured = featured.filter(c =>
    !search || c.display_name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredVerified = verified.filter(c =>
    !search || c.display_name?.toLowerCase().includes(search.toLowerCase())
  );
  const filteredOrgs = allOrgs.filter(o =>
    !manualSearch || o.display_name?.toLowerCase().includes(manualSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 -m-3 sm:-m-6 p-3 sm:p-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-black text-gray-900">Öne Çıkarma & Rozet Yönetimi</h1>
          <p className="text-sm text-gray-500 mt-0.5">Öne çıkan yükleniciler ve doğrulanmış firma rozetlerini yönet</p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" className="gap-2">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          Yenile
        </Button>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={Star} label="Öne Çıkan Firma" value={featured.length} color="bg-amber-50 text-amber-600" />
        <StatCard icon={Shield} label="Doğrulanmış Firma" value={verified.length} color="bg-emerald-50 text-emerald-600" />
        <StatCard icon={DollarSign} label="Öne Çıkarma Fiyatı" value={`$${featuredConfig.price_usd}`} color="bg-blue-50 text-blue-600" />
        <StatCard icon={Award} label="Rozet Fiyatı/Yıl" value={`$${verifiedConfig.price_usd}`} color="bg-purple-50 text-purple-600" />
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-white rounded-xl border border-gray-200 p-1 w-fit">
        {[
          { key: 'featured', label: 'Öne Çıkan', icon: Star },
          { key: 'verified', label: 'Doğrulanmış', icon: Shield },
          { key: 'event-listings', label: 'Etkinlik Listesi', icon: TrendingUp },
          { key: 'settings', label: 'Ayarlar', icon: Settings },
        ].map(({ key, label, icon: Icon }) => (
          <button key={key} onClick={() => setTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${tab === key ? 'bg-gray-900 text-white' : 'text-gray-500 hover:text-gray-900'}`}>
            <Icon className="h-4 w-4" />{label}
          </button>
        ))}
      </div>

      {/* Search + manual action — only for featured/verified tabs */}
      {(tab === 'featured' || tab === 'verified') && (
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Firma ara..." value={search} onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 bg-white" />
          </div>
          <Button onClick={() => openManualModal(tab === 'featured' ? 'featured' : 'verified')}
            className={`gap-2 ${tab === 'featured' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'} text-white`}>
            {tab === 'featured' ? <Star className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
            Manuel {tab === 'featured' ? 'Öne Çıkar' : 'Doğrula'}
          </Button>
        </div>
      )}

      {/* Featured Tab */}
      {tab === 'featured' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin text-gray-300 mx-auto" /></div>
          ) : filteredFeatured.length === 0 ? (
            <div className="p-10 text-center">
              <Star className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">Şu anda öne çıkan firma yok</p>
              <p className="text-xs text-gray-400 mt-1">Firmalar Stripe ödeme veya manuel olarak öne çıkarılabilir</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Firma', 'Konum', 'Bitiş Tarihi', 'Kaynak', 'İşlem'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredFeatured.map(c => (
                  <tr key={c.id} className="hover:bg-amber-50/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                          <Star className="h-4 w-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{c.display_name}</p>
                          <p className="text-xs text-gray-400">{c.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <MapPin className="h-3.5 w-3.5 text-gray-400" />
                        {[c.city, c.country].filter(Boolean).join(', ') || '—'}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        <span className="text-gray-700">
                          {c.featured_until ? new Date(c.featured_until).toLocaleDateString('tr-TR') : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <Badge className={c.featured_note?.includes('Manuel') ? 'bg-purple-100 text-purple-700 border-0' : 'bg-green-100 text-green-700 border-0'}>
                        {c.featured_note?.includes('Manuel') ? 'Manuel' : 'Stripe'}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      <Button size="sm" variant="ghost"
                        onClick={() => handleRemoveFeatured(c.id, c.display_name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 gap-1.5">
                        <X className="h-3.5 w-3.5" />Kaldır
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Verified Tab */}
      {tab === 'verified' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin text-gray-300 mx-auto" /></div>
          ) : filteredVerified.length === 0 ? (
            <div className="p-10 text-center">
              <Shield className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">Henüz doğrulanmış firma yok</p>
              <p className="text-xs text-gray-400 mt-1">Firmalar Stripe ödeme veya manuel olarak doğrulanabilir</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Firma', 'Konum', 'Rozet Bitiş', 'Kaynak', 'İşlem'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filteredVerified.map(c => (
                  <tr key={c.id} className="hover:bg-emerald-50/30 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0">
                          <Shield className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{c.display_name}</p>
                          <p className="text-xs text-gray-400">{c.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5 text-gray-600">
                        <MapPin className="h-3.5 w-3.5 text-gray-400" />
                        {[c.city, c.country].filter(Boolean).join(', ') || '—'}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5 text-gray-400" />
                        <span className="text-gray-700">
                          {c.verified_until ? new Date(c.verified_until).toLocaleDateString('tr-TR') : '—'}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <Badge className={c.verified_note?.includes('Manuel') ? 'bg-purple-100 text-purple-700 border-0' : 'bg-green-100 text-green-700 border-0'}>
                        {c.verified_note?.includes('Manuel') ? 'Manuel' : 'Stripe'}
                      </Badge>
                    </td>
                    <td className="px-5 py-4">
                      <Button size="sm" variant="ghost"
                        onClick={() => handleRemoveVerified(c.id, c.display_name)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 gap-1.5">
                        <X className="h-3.5 w-3.5" />Kaldır
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {tab === 'settings' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {/* Featured Listing Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-amber-50 flex items-center justify-center">
                <Star className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Öne Çıkarma Ayarları</h3>
                <p className="text-xs text-gray-500">Contractor leaderboard</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Fiyat (USD)</label>
                <Input type="number" min="1" value={featuredConfig.price_usd}
                  onChange={e => setFeaturedConfig(p => ({ ...p, price_usd: parseFloat(e.target.value) || 0 }))}
                  className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Süre (Gün)</label>
                <Input type="number" min="1" value={featuredConfig.duration_days}
                  onChange={e => setFeaturedConfig(p => ({ ...p, duration_days: parseInt(e.target.value) || 30 }))}
                  className="h-10" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">Aktif</span>
                <button onClick={() => setFeaturedConfig(p => ({ ...p, is_active: !p.is_active }))}
                  className={`w-11 h-6 rounded-full transition-colors ${featuredConfig.is_active ? 'bg-amber-500' : 'bg-gray-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${featuredConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <Button onClick={saveFeaturedConfig} disabled={saving} className="w-full bg-amber-500 hover:bg-amber-600 text-white gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Kaydet
              </Button>
            </div>
          </div>

          {/* Verified Badge Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Doğrulanmış Rozet</h3>
                <p className="text-xs text-gray-500">Yıllık rozet aboneliği</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Fiyat (USD / Yıl)</label>
                <Input type="number" min="1" value={verifiedConfig.price_usd}
                  onChange={e => setVerifiedConfig(p => ({ ...p, price_usd: parseFloat(e.target.value) || 0 }))}
                  className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Geçerlilik (Gün)</label>
                <Input type="number" min="1" value={verifiedConfig.duration_days}
                  onChange={e => setVerifiedConfig(p => ({ ...p, duration_days: parseInt(e.target.value) || 365 }))}
                  className="h-10" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">Aktif</span>
                <button onClick={() => setVerifiedConfig(p => ({ ...p, is_active: !p.is_active }))}
                  className={`w-11 h-6 rounded-full transition-colors ${verifiedConfig.is_active ? 'bg-emerald-500' : 'bg-gray-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${verifiedConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <Button onClick={saveVerifiedConfig} disabled={saving} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Tümünü Kaydet
              </Button>
            </div>
          </div>

          {/* Offer Commission Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Teklif Komisyonu</h3>
                <p className="text-xs text-gray-500">Kabul edilen teklifler için komisyon</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Komisyon Oranı (%)</label>
                <Input type="number" min="0" max="30" step="0.5" value={commissionConfig.rate_percent}
                  onChange={e => setCommissionConfig(p => ({ ...p, rate_percent: parseFloat(e.target.value) || 0 }))}
                  className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Minimum Ücret (USD)</label>
                <Input type="number" min="0" value={commissionConfig.min_fee_usd}
                  onChange={e => setCommissionConfig(p => ({ ...p, min_fee_usd: parseFloat(e.target.value) || 0 }))}
                  className="h-10" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">Aktif</span>
                <button onClick={() => setCommissionConfig(p => ({ ...p, is_active: !p.is_active }))}
                  className={`w-11 h-6 rounded-full transition-colors ${commissionConfig.is_active ? 'bg-orange-500' : 'bg-gray-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${commissionConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <Button onClick={saveVerifiedConfig} disabled={saving} className="w-full bg-orange-500 hover:bg-orange-600 text-white gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Kaydet
              </Button>
            </div>
          </div>

          {/* Event Listing Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Etkinlik Listeleme</h3>
                <p className="text-xs text-gray-500">Organizatör etkinlik listeleme ücreti</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Fiyat (USD)</label>
                <Input type="number" min="1" value={eventListingConfig.price_usd}
                  onChange={e => setEventListingConfig(p => ({ ...p, price_usd: parseFloat(e.target.value) || 0 }))}
                  className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Süre (Gün)</label>
                <Input type="number" min="1" value={eventListingConfig.duration_days}
                  onChange={e => setEventListingConfig(p => ({ ...p, duration_days: parseInt(e.target.value) || 90 }))}
                  className="h-10" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">Aktif</span>
                <button onClick={() => setEventListingConfig(p => ({ ...p, is_active: !p.is_active }))}
                  className={`w-11 h-6 rounded-full transition-colors ${eventListingConfig.is_active ? 'bg-blue-500' : 'bg-gray-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${eventListingConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <Button onClick={saveVerifiedConfig} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Kaydet
              </Button>
            </div>
          </div>

          {/* Analytics Premium Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Analytics Premium</h3>
                <p className="text-xs text-gray-500">Aylık gelişmiş analitik erişimi</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Fiyat (USD / Ay)</label>
                <Input type="number" min="1" value={analyticsPremiumConfig.price_usd}
                  onChange={e => setAnalyticsPremiumConfig(p => ({ ...p, price_usd: parseFloat(e.target.value) || 0 }))}
                  className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1.5 block">Süre (Gün)</label>
                <Input type="number" min="1" value={analyticsPremiumConfig.duration_days}
                  onChange={e => setAnalyticsPremiumConfig(p => ({ ...p, duration_days: parseInt(e.target.value) || 30 }))}
                  className="h-10" />
              </div>
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                <span className="text-sm font-medium text-gray-700">Aktif</span>
                <button onClick={() => setAnalyticsPremiumConfig(p => ({ ...p, is_active: !p.is_active }))}
                  className={`w-11 h-6 rounded-full transition-colors ${analyticsPremiumConfig.is_active ? 'bg-purple-500' : 'bg-gray-200'}`}>
                  <div className={`w-5 h-5 bg-white rounded-full shadow transition-transform mx-0.5 ${analyticsPremiumConfig.is_active ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              <Button onClick={saveVerifiedConfig} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-700 text-white gap-2">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Kaydet
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Event Listings Tab */}
      {tab === 'event-listings' && (
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center"><Loader2 className="h-8 w-8 animate-spin text-gray-300 mx-auto" /></div>
          ) : eventListings.length === 0 ? (
            <div className="p-10 text-center">
              <TrendingUp className="h-10 w-10 text-gray-200 mx-auto mb-3" />
              <p className="text-gray-400 font-medium">Aktif etkinlik listeleme yok</p>
              <p className="text-xs text-gray-400 mt-1">Organizatörler Stripe ödeme ile etkinliklerini listeleyebilir</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  {['Etkinlik', 'Org ID', 'Bitiş Tarihi', 'Durum'].map(h => (
                    <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {eventListings.map(ev => (
                  <tr key={ev.id} className="hover:bg-blue-50/20 transition-colors">
                    <td className="px-5 py-4 font-semibold text-gray-900">{ev.event_name}</td>
                    <td className="px-5 py-4 text-xs text-gray-500">{ev.org_id}</td>
                    <td className="px-5 py-4 text-gray-600">{ev.listing_until ? new Date(ev.listing_until).toLocaleDateString('tr-TR') : '—'}</td>
                    <td className="px-5 py-4"><Badge className="bg-green-100 text-green-700 border-0">{ev.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Manual Modal */}
      {manualModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h3 className="font-bold text-gray-900">
                {manualModal === 'featured' ? 'Firma Öne Çıkar' : 'Firma Doğrula'}
              </h3>
              <button onClick={() => setManualModal(null)} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input placeholder="Firma ara..." value={manualSearch} onChange={e => setManualSearch(e.target.value)}
                  className="pl-9 h-10" />
              </div>
              <div className="max-h-60 overflow-y-auto space-y-1.5 border border-gray-100 rounded-xl p-2">
                {orgsLoading ? (
                  <div className="text-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-300 mx-auto" /></div>
                ) : filteredOrgs.slice(0, 20).map(org => (
                  <button key={org.id} onClick={() => setSelectedOrg(org)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors
                      ${selectedOrg?.id === org.id ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50'}`}>
                    <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0">
                      <Building2 className="h-4 w-4 text-gray-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{org.display_name}</p>
                      <p className="text-xs text-gray-400">{[org.city, org.country].filter(Boolean).join(', ')}</p>
                    </div>
                    {selectedOrg?.id === org.id && <CheckCircle className="h-4 w-4 text-green-600 shrink-0 ml-auto" />}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setManualModal(null)} className="flex-1">İptal</Button>
                <Button onClick={handleManualAction} disabled={!selectedOrg}
                  className={`flex-1 text-white gap-2 ${manualModal === 'featured' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}>
                  {manualModal === 'featured' ? <Star className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                  {manualModal === 'featured' ? 'Öne Çıkar' : 'Doğrula'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
